import React, { useContext, useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";

import Card from "../../shared/components/UIElements/Card";
import { AuthContext } from "../../shared/context/auth-context";
import { formatDateTime } from "../pages/admin/adminUi";
import "./BacktestOverview.css";

// The backtest at the top of /portfolio (GET /api/dashboard/backtest_summary,
// written by control after each fund rebuild:
// control/src/services/backtest_summary.py). The figures and the panels cover
// the whole backtest; the period buttons choose what the charts and the
// calendar show.

// On white cards, like /reports. The fund: the app's accent a step darker,
// since #00d97e has too little contrast on white (1.9:1). The benchmark is
// context: grey next to the fund, in every chart. Gains and losses: blue and
// red with grey at 0. All checked against white (lightness, contrast,
// colour-blind separation).
const FUND = "#00a86b";
const BENCH = "#888888";
const GAIN = "#0096e6";
const LOSS = "#ff4757";
const GAIN_RGB = "0, 150, 230";
const LOSS_RGB = "255, 71, 87";
const TICKS = "#888888";
const GRID = "#ececec";

const RANGES = [
  { key: "1y", label: "1Y", years: 1 },
  { key: "3y", label: "3Y", years: 3 },
  { key: "5y", label: "5Y", years: 5 },
  { key: "10y", label: "10Y", years: 10 },
  { key: "all", label: "All", years: null },
];
const MAX_POINTS = 1500; // per line; longer periods are thinned
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Formatting ───────────────────────────────────────────────────────────────

const pct = (x, digits = 1) => (x == null ? "—" : `${(x * 100).toFixed(digits)} %`);
const signedPct = (x, digits = 1) => (x == null ? "—" : `${x > 0 ? "+" : ""}${(x * 100).toFixed(digits)} %`);
const pp = (x, digits = 1) => (x == null ? "—" : `${x > 0 ? "+" : ""}${(x * 100).toFixed(digits)} pp`);
const ratio = (x) => (x == null ? "—" : x.toFixed(2));
const MONTH = new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
const month = (iso) => (iso ? MONTH.format(new Date(iso)) : "—");
const day = (iso) => (iso ? DAY.format(new Date(iso)) : "—");
const years = (days) => `${(days / 365.25).toFixed(1)} years`;
const tone = (x) => (x > 0 ? "bt-good" : x < 0 ? "bt-bad" : "");

// ─── Series for the charts ────────────────────────────────────────────────────

const minusYears = (iso, n) => {
  const d = new Date(iso);
  d.setUTCFullYear(d.getUTCFullYear() - n);
  return d.toISOString().slice(0, 10);
};

// The fund's return over the past year minus the benchmark's, on each day
export function rollingExcess({ dates, fund, benchmark }) {
  const out = new Array(dates.length).fill(null);
  let j = 0;
  for (let i = 0; i < dates.length; i += 1) {
    const back = minusYears(dates[i], 1);
    if (dates[0] > back) continue;
    while (j + 1 < dates.length && dates[j + 1] <= back) j += 1;
    if (benchmark[i] != null && benchmark[j] != null) {
      out[i] = (fund[i] / fund[j] - 1) - (benchmark[i] / benchmark[j] - 1);
    }
  }
  return out;
}

// The days of the period: both series rebased to 100 at its start (the
// benchmark at its first value in it), drawdowns from the highs in it, the
// invested share and the rolling excess return
export function windowed(series, years, rolling) {
  const { dates, fund, benchmark, cash_ratio: cash } = series;
  let start = 0;
  if (years) {
    const from = minusYears(dates[dates.length - 1], years);
    start = Math.max(0, dates.findIndex((d) => d >= from));
  }
  const bStart = benchmark.slice(start).find((b) => b != null);
  const points = [];
  let fPeak = -Infinity, bPeak = -Infinity;
  for (let i = start; i < dates.length; i += 1) {
    const f = fund[i], b = benchmark[i];
    fPeak = Math.max(fPeak, f);
    if (b != null) bPeak = Math.max(bPeak, b);
    points.push({
      date: dates[i],
      fund: (f / fund[start]) * 100,
      bench: b != null && bStart ? (b / bStart) * 100 : null,
      fundDd: f / fPeak - 1,
      benchDd: b != null ? b / bPeak - 1 : null,
      invested: 1 - cash[i],
      excess: rolling[i],
    });
  }
  return points;
}

// At most MAX_POINTS: the last day of each stretch, its lowest drawdowns, so
// the troughs stay
export function thin(points) {
  const step = Math.ceil(points.length / MAX_POINTS);
  if (step <= 1) return points;
  const lowest = (bucket, key) => bucket.reduce((m, p) => (p[key] != null && (m == null || p[key] < m) ? p[key] : m), null);
  const out = [];
  for (let i = 0; i < points.length; i += step) {
    const bucket = points.slice(i, i + step);
    out.push({ ...bucket[bucket.length - 1], fundDd: lowest(bucket, "fundDd"), benchDd: lowest(bucket, "benchDd") });
  }
  return out;
}

// ─── Charts ───────────────────────────────────────────────────────────────────

const axis = {
  ticks: { color: TICKS, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 16 },
  grid: { color: GRID },
  border: { display: false },
};
const timeAxis = (count) => ({
  ...axis,
  ticks: {
    ...axis.ticks,
    maxTicksLimit: 8,
    callback(value) {
      const label = this.getLabelForValue(value);
      return count > 800 ? label.slice(0, 4) : month(label);
    },
  },
});
const percentAxis = (format = (v) => pct(v, 0)) => ({ ...axis, ticks: { ...axis.ticks, callback: format } });
const options = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: { mode: "index", intersect: false },
  plugins: { legend: { labels: { color: "#555555", boxWidth: 10, font: { size: 11 } } } },
  ...extra,
});
const withTooltip = (label, legend = true) => ({
  ...options().plugins,
  ...(legend ? {} : { legend: { display: false } }),
  tooltip: { callbacks: { title: (items) => day(items[0]?.label), label } },
});
// backgroundColor fills the legend's square (and the area, where fill is set)
const line = (label, data, color, extra = {}) => ({
  label, data, borderColor: color, backgroundColor: color, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4,
  tension: 0, spanGaps: true, ...extra,
});

function GrowthChart({ points, log, benchName }) {
  const labels = points.map((p) => p.date);
  const lead = (v) => Math.round(v / 10 ** Math.floor(Math.log10(v)));
  return (
    <Line
      role="img" aria-label={`Growth of 100: the fund and the ${benchName}`}
      data={{
        labels,
        datasets: [
          line("Fund", points.map((p) => p.fund), FUND),
          line(benchName, points.map((p) => p.bench), BENCH, { borderWidth: 1.5 }),
        ],
      }}
      options={options({
        plugins: withTooltip((c) => `${c.dataset.label}: ${c.parsed.y == null ? "—" : c.parsed.y.toFixed(1)}`),
        scales: {
          x: timeAxis(labels.length),
          y: {
            ...axis,
            type: log ? "logarithmic" : "linear",
            ticks: {
              ...axis.ticks,
              callback: (v) => (!log || [1, 2, 5].includes(lead(v)) ? v.toLocaleString("en-GB", { maximumFractionDigits: 0 }) : ""),
            },
          },
        },
      })}
    />
  );
}

function DrawdownChart({ points, benchName }) {
  const labels = points.map((p) => p.date);
  return (
    <Line
      role="img" aria-label={`Drawdown from the last high: the fund and the ${benchName}`}
      data={{
        labels,
        datasets: [
          line("Fund", points.map((p) => p.fundDd), FUND, { borderWidth: 1.5, fill: "origin", backgroundColor: "rgba(0, 168, 107, 0.14)" }),
          line(benchName, points.map((p) => p.benchDd), BENCH, { borderWidth: 1.5 }),
        ],
      }}
      options={options({
        plugins: withTooltip((c) => `${c.dataset.label}: ${pct(c.parsed.y)}`),
        scales: { x: timeAxis(labels.length), y: { ...percentAxis(), max: 0 } },
      })}
    />
  );
}

function ExcessByYear({ rows, benchName }) {
  const data = rows.filter((r) => r.excess != null);
  return (
    <Bar
      role="img" aria-label={`The fund's return minus the ${benchName}'s, each year`}
      data={{
        labels: data.map((r) => String(r.year)),
        datasets: [{
          label: `Fund minus ${benchName}`, data: data.map((r) => r.excess),
          backgroundColor: data.map((r) => (r.excess >= 0 ? GAIN : LOSS)), borderRadius: 2,
        }],
      }}
      options={options({
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `Excess: ${pp(c.parsed.y)}` } } },
        scales: { x: { ...axis, grid: { display: false } }, y: percentAxis((v) => pp(v, 0)) },
      })}
    />
  );
}

function SmallLine({ points, value, label, color, format, fill, min, max }) {
  const labels = points.map((p) => p.date);
  return (
    <Line
      role="img" aria-label={label}
      data={{
        labels,
        datasets: [line(label, points.map(value), color, {
          borderWidth: 1.5, ...(fill ? { fill: "origin", backgroundColor: fill } : {}),
        })],
      }}
      options={options({
        plugins: withTooltip((c) => `${label}: ${format(c.parsed.y)}`, false),
        scales: { x: timeAxis(labels.length), y: { ...percentAxis((v) => format(v)), min, max } },
      })}
    />
  );
}

// ─── Tables and lists ─────────────────────────────────────────────────────────

const cellStyle = (r) => {
  if (r == null) return undefined;
  const alpha = Math.min(Math.abs(r) / 0.1, 1) * 0.55;
  return { background: `rgba(${r >= 0 ? GAIN_RGB : LOSS_RGB}, ${alpha.toFixed(3)})` };
};

function CalendarTable({ rows, benchName }) {
  return (
    <div className="bt-table-wrap">
      <table className="bt-table bt-calendar">
        <caption className="bt-note">Monthly returns of the fund; the year of the fund and of the {benchName}, and the difference</caption>
        <thead>
          <tr>
            <th scope="col">Year</th>
            {MONTHS.map((m) => <th key={m} scope="col">{m}</th>)}
            <th scope="col">Fund</th>
            <th scope="col">{benchName}</th>
            <th scope="col">Excess</th>
          </tr>
        </thead>
        <tbody>
          {[...rows].reverse().map((r) => (
            <tr key={r.year}>
              <th scope="row">{r.year}</th>
              {r.months.map((m, i) => <td key={i} style={cellStyle(m)}>{m == null ? "" : (m * 100).toFixed(1)}</td>)}
              <td className="bt-calendar__year" style={cellStyle(r.fund)}>{signedPct(r.fund)}</td>
              <td>{signedPct(r.benchmark)}</td>
              <td className={tone(r.excess)}>{pp(r.excess)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DrawdownTable({ rows }) {
  return (
    <table className="bt-table bt-drawdowns">
      <thead>
        <tr><th scope="col">Depth</th><th scope="col">High</th><th scope="col">Low</th><th scope="col">Recovered</th><th scope="col">Length</th></tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.peak}>
            <td className="bt-bad">{pct(d.depth)}</td>
            <td>{month(d.peak)}</td>
            <td>{month(d.trough)}</td>
            <td>{d.recovery ? month(d.recovery) : "not yet"}</td>
            <td>{d.days > 365 ? years(d.days) : `${d.days} days`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Stats({ rows }) {
  return (
    <dl className="bt-stats">
      {rows.map(([label, value, title]) => (
        <div key={label}>
          <dt title={title}>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const STATUS = { ok: ["✓", "OK"], caution: ["!", "Caution"], missing: ["✕", "Missing"], info: ["i", "Note"] };

function Trust({ status, title, children }) {
  const [icon, label] = STATUS[status];
  return (
    <li className={`bt-trust__row bt-trust__row--${status}`}>
      <span className="bt-trust__icon" role="img" aria-label={label}>{icon}</span>
      <div>
        <span className="bt-trust__title">{title}</span>
        <span className="bt-trust__detail">{children}</span>
      </div>
    </li>
  );
}

// ─── Page section ─────────────────────────────────────────────────────────────

// A headline figure; one of them is the hero
function Figure({ label, value, sub, hero }) {
  return (
    <div className={`bt-figure${hero ? " bt-figure--hero" : ""}`}>
      <span className="bt-figure__label">{label}</span>
      <span className="bt-figure__value">{value}</span>
      {sub && <span className="bt-figure__sub">{sub}</span>}
    </div>
  );
}

function Title({ children, note }) {
  return <h2 className="bt-title">{children}{note && <span>{note}</span>}</h2>;
}

function Loading() {
  return (
    <section className="bt-page" aria-busy="true" aria-label="Backtest">
      <Card className="bt-card"><span className="bt-skeleton bt-skeleton--title" /><span className="bt-skeleton bt-skeleton--row" /></Card>
      <Card className="bt-card"><span className="bt-skeleton bt-skeleton--chart" /></Card>
    </section>
  );
}

function Message({ children, role }) {
  return (
    <section className="bt-page" aria-label="Backtest">
      <Card className="bt-card bt-message"><div role={role}>{children}</div></Card>
    </section>
  );
}

const BacktestOverview = () => {
  const auth = useContext(AuthContext);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null); // { status, message }
  const [reloadKey, setReloadKey] = useState(0);
  const [range, setRange] = useState("all");
  const [log, setLog] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetch("/api/dashboard/backtest_summary", {
      headers: { Authorization: "Bearer " + auth.token },
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw Object.assign(new Error(body.message || `HTTP ${res.status}`), { status: res.status });
        setSummary(body.summary);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError({ status: err.status || 0, message: err.message });
      });
    return () => controller.abort();
  }, [auth.token, reloadKey]);

  const series = summary?.series;
  const rolling = useMemo(() => (series ? rollingExcess(series) : []), [series]);
  const period = RANGES.find((r) => r.key === range);
  const points = useMemo(
    () => (series ? thin(windowed(series, period.years, rolling)) : []),
    [series, period, rolling],
  );

  if (error?.status === 404) {
    return (
      <Message>
        <p className="bt-message__title">There's no backtest summary yet.</p>
        <p className="bt-note">It's written with the next fund rebuild: at night, or after an instrument is added or removed.</p>
      </Message>
    );
  }
  if (error) {
    return (
      <Message role="alert">
        <p className="bt-message__title">Couldn't load the backtest: {error.message}</p>
        <button type="button" className="bt-button" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
      </Message>
    );
  }
  if (!summary) return <Loading />;

  const { fund, benchmark: bench, relative: rel, trades, exposure, what_ifs: w = {}, reliability: trust } = summary;
  const benchName = summary.benchmark_name;
  const dd = fund.max_drawdown;
  const firstYear = points.length ? Number(points[0].date.slice(0, 4)) : null;
  const calendarRows = summary.calendar.filter((r) => firstYear == null || r.year >= firstYear);
  const from = fund.from.slice(0, 4), to = fund.to.slice(0, 4);

  return (
    <section className="bt-page" aria-labelledby="bt-heading">
      <Card className="bt-card bt-head">
        <h1 id="bt-heading">Backtest</h1>
        <p>
          {day(fund.from)} – {day(fund.to)} · {summary.instruments} instruments (today's list) · {summary.fund_label}
          {" "}· {summary.currency} · rebuilt {formatDateTime(summary.run?.finished_at || summary.created_at)}
          {summary.run?.trigger ? ` (${summary.run.trigger})` : ""}
        </p>
      </Card>

      <Card className="bt-card">
        <div className="bt-figures">
          <Figure
            hero label="Return a year (CAGR)" value={pct(fund.cagr)}
            sub={`${bench ? `${benchName} ${pct(bench.cagr)} · ` : ""}×${(1 + fund.total_return).toFixed(1)} since ${from}`}
          />
          <Figure
            label={`Excess vs ${benchName}`}
            value={<span className={tone(rel?.excess_cagr)}>{pp(rel?.excess_cagr)}</span>}
            sub={rel ? `a year, before costs · alpha ${signedPct(rel.alpha)}` : "no benchmark data yet"}
          />
          <Figure
            label="Max drawdown"
            value={<span className="bt-bad">{pct(dd?.depth)}</span>}
            sub={dd ? `${month(dd.peak)} – ${month(dd.trough)}, ${dd.recovery ? `${years(dd.days)} under water` : "not recovered"}`
              + (bench?.max_drawdown ? ` · ${benchName} ${pct(bench.max_drawdown.depth)}` : "") : "none"}
          />
          <Figure label="Volatility" value={pct(fund.volatility)} sub={bench ? `a year · ${benchName} ${pct(bench.volatility)}` : "a year"} />
          <Figure
            label="Sharpe ratio" value={ratio(fund.sharpe)}
            sub={`${bench ? `${benchName} ${ratio(bench.sharpe)} · ` : ""}Sortino ${ratio(fund.sortino)} · Calmar ${ratio(fund.calmar)}`}
          />
          <Figure
            label="Now"
            value={<span className={fund.current_drawdown < 0 ? "bt-bad" : ""}>{fund.current_drawdown < 0 ? pct(fund.current_drawdown) : "at a high"}</span>}
            sub={fund.current_drawdown < 0 ? "below the last high" : `on ${day(fund.to)}`}
          />
        </div>
      </Card>

      <Card className="bt-card">
        <div className="bt-controls">
          <Title note={`the fund and the ${benchName} (dividends reinvested), USD`}>Growth of 100</Title>
          <div className="bt-periods" role="group" aria-label="Period of the charts">
            {RANGES.map((r) => (
              <button key={r.key} type="button" className={range === r.key ? "is-active" : ""} aria-pressed={range === r.key}
                      onClick={() => setRange(r.key)}>{r.label}</button>
            ))}
          </div>
          <label className="bt-check">
            <input type="checkbox" checked={log} onChange={(e) => setLog(e.target.checked)} /> Log scale
          </label>
        </div>
        <p className="bt-note">The charts and the calendar show the period; the figures and the panels cover {from}–{to}.</p>
        <div className="bt-chart bt-chart--tall"><GrowthChart points={points} log={log} benchName={benchName} /></div>
      </Card>

      <Card className="bt-card">
        <div className="bt-split">
          <div>
            <Title note="drawdown from the last high in the period">Under water</Title>
            <div className="bt-chart"><DrawdownChart points={points} benchName={benchName} /></div>
          </div>
          <div>
            <Title note={`since ${from}`}>Deepest drawdowns</Title>
            <DrawdownTable rows={summary.drawdowns} />
          </div>
        </div>
      </Card>

      <Card className="bt-card">
        <Title note={`the fund's return minus the ${benchName}'s`}>Year by year</Title>
        <div className="bt-chart bt-chart--short"><ExcessByYear rows={calendarRows} benchName={benchName} /></div>
        <CalendarTable rows={calendarRows} benchName={benchName} />
      </Card>

      <Card className="bt-card">
        <div className="bt-columns">
          <div>
            <Title note={`${trades.closed} closed · ${trades.open} open`}>The fund's trades</Title>
            {trades.closed > 0 ? (
              <Stats rows={[
                ["Ended with a gain", pct(trades.win_rate, 0)],
                ["Average gain / loss", `${signedPct(trades.avg_gain)} / ${signedPct(trades.avg_loss)}`],
                ["Per trade", `${signedPct(trades.mean)} on average, ${signedPct(trades.median)} median`],
                ["Gains over losses", ratio(trades.profit_factor), "The sum of the gains over the sum of the losses, each trade counting the same (profit factor)"],
                ["Held", `${Math.round(trades.median_days)} days median`],
                ["Best", `${trades.best.ticker} ${signedPct(trades.best.return, 0)} · ${month(trades.best.entry)}, ${trades.best.days} days`],
                ["Worst", `${trades.worst.ticker} ${signedPct(trades.worst.return, 0)} · ${month(trades.worst.entry)}, ${trades.worst.days} days`],
              ]} />
            ) : <p className="bt-note">No closed trades yet.</p>}
          </div>
          <div>
            <Title note={`since ${from}`}>Exposure</Title>
            <Stats rows={[
              ["Invested", `${pct(exposure.invested, 0)} on average`],
              ["Positions", `${exposure.positions_avg.toFixed(1)} on average, ${exposure.positions_max} at most`],
              ["BUY signals funded", exposure.signals_funded == null ? "—" : `${pct(exposure.signals_funded, 0)} (${exposure.buys} of ${exposure.buy_signals})`,
                "A BUY the fund can't pay for is dropped; with more instruments, more signals compete for the same cash"],
              ["Traded a year", exposure.turnover == null ? "—" : `${exposure.turnover.toFixed(1)} × the fund's value`],
            ]} />
            <div className="bt-chart bt-chart--small">
              <SmallLine points={points} value={(p) => p.invested} label="Invested" color={GAIN} fill="rgba(0, 150, 230, 0.12)"
                         format={(v) => pct(v, 0)} min={0} max={1} />
            </div>
          </div>
        </div>
      </Card>

      <Card className="bt-card">
        <Title note="on the days both have">Against the {benchName}</Title>
        {rel ? (
          <div className="bt-columns">
            <Stats rows={[
              ["Beta · correlation", `${ratio(rel.beta)} · ${ratio(rel.correlation)}`],
              ["Alpha", `${signedPct(rel.alpha)} a year`, "Jensen's alpha: the return above what the beta explains, over the risk-free rate"],
              ["Tracking error", `${pct(rel.tracking_error)} a year`],
              ["Information ratio", ratio(rel.information_ratio)],
              ["Up / down capture", `${pct(rel.up_capture, 0)} / ${pct(rel.down_capture, 0)}`, "The fund's average month as a share of the benchmark's, in its up and in its down months"],
              ["Months ahead of it", pct(rel.months_beating, 0)],
            ]} />
            <div className="bt-chart">
              <SmallLine points={points} value={(p) => p.excess} label={`12 months, minus the ${benchName}`} color={GAIN}
                         format={(v) => pp(v, 0)} />
            </div>
          </div>
        ) : <p className="bt-note">The benchmark is fetched with the next nightly run.</p>}
      </Card>

      <Card className="bt-card">
        <Title>How far to trust it</Title>
        <ul className="bt-trust">
          {trust.benchmark ? (
            <Trust status="ok" title="Benchmark with dividends">
              {benchName} ({summary.benchmark_symbol}), like the fund's prices, which include dividends.
            </Trust>
          ) : (
            <Trust status="missing" title="No benchmark yet">It's fetched with the next nightly run.</Trust>
          )}
          <Trust status="caution" title="No trading costs">
            {w.costs
              ? `With ${w.costs.cost_bps} bps per side (commission, spread, slippage): CAGR ${pct(w.costs.cagr)} (${pp(w.costs.cagr_change, 2)}).`
              : "Not charged."}
          </Trust>
          <Trust status="caution" title="Filled at the signal day's close">
            {w.next_day
              ? `The trader buys the next day. Filled a day later: CAGR ${pct(w.next_day.cagr)} (${pp(w.next_day.cagr_change, 2)}).`
                + (w.both ? ` Both: ${pct(w.both.cagr)} (${pp(w.both.cagr_change, 2)})${w.both.excess_cagr != null ? `, ${pp(w.both.excess_cagr)} a year over the ${benchName}` : ""}.` : "")
              : "The trader buys the next day."}
          </Trust>
          <Trust status="missing" title="Today's instruments, back to the start">
            The {summary.instruments} instruments of today are tested from {from} on, so the list already knows which
            companies did well (survivorship). Not measurable without the list as it was on each date.
          </Trust>
          <Trust status={trust.risk_free ? "ok" : "caution"} title="Risk-free rate">
            {trust.risk_free
              ? `13-week US T-bill (^IRX), ${pct(fund.risk_free_rate)} a year on average, in the Sharpe and Sortino ratios.`
              : "None yet: the Sharpe and Sortino ratios use 0 %."}
          </Trust>
          <Trust status="info" title="Sharpe ratio above 0">
            {`Probability ${pct(trust.psr)}; deflated for at least ${trust.trials} fund ${trust.trials === 1 ? "configuration" : "configurations"} tried: ${pct(trust.dsr)}.`}
            {" "}Both assume the instruments weren't chosen with hindsight.
          </Trust>
          <Trust status="info" title="Published record">
            {trust.published
              ? `${trust.published.days} days since ${day(trust.published.since)}: ${signedPct(trust.published.published_return)} as published, `
                + `${signedPct(trust.published.backtest_return)} in today's backtest of the same days.`
              : "Fewer than two published days so far."}
          </Trust>
        </ul>
      </Card>
    </section>
  );
};

export default BacktestOverview;
